import type {
  CreateShipmentDropOffInformation,
  CreateShipmentInitialRequest,
  CustomerPickupType,
} from '../../../core/api/shipment/models/create-shipment.model';
import type { ShipmentFlowState } from '../../../core/services/shipment-flow-state.service';
import type { InductionMethod } from '../../service-step/sections/pickup-drop-off/pickup-drop-off.state';

const INCOMPLETE_STATE_MESSAGE = 'Required shipment state is incomplete.';

/** Maps Service-step kebab induction methods to create-request `pickupType` (OM-774). */
const PICKUP_TYPE_BY_METHOD: Record<InductionMethod, CustomerPickupType> = {
  'drop-off': 'DROP_OFF',
  'label-only': 'CREATE_LABEL',
  pickup: 'PICKUP',
};

/**
 * Normalizes the selected expected-delivery date to the backend YYYY-MM-DD contract.
 */
export function normalizeDeliveryDate(value: string | undefined): string {
  const normalized = value?.trim().replaceAll('/', '-') ?? '';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    throw new Error(INCOMPLETE_STATE_MESSAGE);
  }
  const [year, month, day] = normalized.split('-').map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    throw new Error(INCOMPLETE_STATE_MESSAGE);
  }
  return normalized;
}

/**
 * Returns a required trimmed value or rejects shipment creation before any API call.
 */
function required(value: string | undefined): string {
  const normalized = value?.trim() ?? '';
  if (!normalized) {
    throw new Error(INCOMPLETE_STATE_MESSAGE);
  }
  return normalized;
}

/**
 * Maps the UI induction method to the create-request `pickupType` enum.
 * @throws When the method is missing or unrecognized.
 */
function mapPickupType(method: InductionMethod | undefined): CustomerPickupType {
  if (!method || !(method in PICKUP_TYPE_BY_METHOD)) {
    throw new Error(INCOMPLETE_STATE_MESSAGE);
  }
  return PICKUP_TYPE_BY_METHOD[method];
}

/**
 * Builds LocatorAddress-shaped `dropOffInformation` from a persisted nearest drop-off.
 * Returns undefined when locator search failed or the address is incomplete so create can
 * still send `pickupType=DROP_OFF` without a location.
 */
function mapDropOffInformation(
  nearestDropOff: { address?: CreateShipmentDropOffInformation } | undefined,
): CreateShipmentDropOffInformation | undefined {
  const address = nearestDropOff?.address;
  if (!address) {
    return undefined;
  }

  const streetNumber = address.streetNumber?.trim() ?? '';
  const streetName = address.streetName?.trim() ?? '';
  const streetType = address.streetType?.trim() ?? '';
  const municipalityName = address.municipalityName?.trim() ?? '';
  const provinceCode = address.provinceCode?.trim() ?? '';
  const postalCode = address.postalCode?.trim() ?? '';
  const countryCode = address.countryCode?.trim() ?? '';

  if (
    !streetNumber ||
    !streetName ||
    !streetType ||
    !municipalityName ||
    !provinceCode ||
    !postalCode ||
    !countryCode
  ) {
    return undefined;
  }

  return {
    streetNumber,
    streetName,
    streetType,
    municipalityName,
    provinceCode,
    postalCode,
    countryCode,
  };
}

/**
 * Maps persisted wizard facts to the complete Shipment Manager initial request.
 * Attestations stay opaque; the browser only selects the product JWT by product code.
 * Maps UI kebab induction methods to create `pickupType` and attaches drop-off address when available.
 */
export function mapCreateShipmentRequest(
  state: ShipmentFlowState | null,
  language: 'en' | 'fr',
): CreateShipmentInitialRequest {
  const selectedService = state?.selectedService;
  const addresses = state?.addresses;
  const attestations = state?.attestations;
  const productCode = selectedService?.productCode;
  const pickupType = mapPickupType(state?.pickupDropOff?.method);

  const request: CreateShipmentInitialRequest = {
    serviceAbilityToken: required(attestations?.serviceAbility),
    packageSelectionToken: required(attestations?.packageSelection),
    productSelectionToken: required(
      productCode ? attestations?.productSelectionByProductCode?.[productCode] : undefined,
    ),
    estimateToken: required(attestations?.estimate),
    selectedExpectedDeliveryDate: normalizeDeliveryDate(selectedService?.deliveryDate),
    accountToken: required(attestations?.account),
    pickupType,
    language,
  };

  if (pickupType === 'DROP_OFF') {
    const dropOffInformation = mapDropOffInformation(state?.pickupDropOff?.nearestDropOff);
    if (dropOffInformation) {
      request.dropOffInformation = dropOffInformation;
    }
  }

  if (pickupType === 'PICKUP') {
    request.pickupValidationToken = required(attestations?.pickupValidation);
  }

  const references = (state?.package?.trackingReferences ?? [])
    .map((value) => value.trim())
    .filter(Boolean);
  if (references.length > 0) {
    request.references = Object.fromEntries(
      references.map((value, index) => [`reference${index + 1}`, value]),
    );
  }

  const deliveryInstructions = state?.package?.deliveryInstructions?.trim();
  if (deliveryInstructions) {
    request.deliveryInstructions = deliveryInstructions;
  }

  if (addresses) {
    request.notificationPreferences = {
      senderReceiveUpdates: addresses.shipFrom.receiveNotifications,
      recipientReceiveUpdates: addresses.shipTo.receiveNotifications,
    };
  }

  return request;
}
